import { useEffect, useState } from "react";
import { useFormik } from "formik";
import { useHistory } from "react-router-dom";
import * as Yup from "yup";
import {
  EntryForm,
  Column,
  Input,
  CheckBox,
} from "../components/EntryForm";
import Page from "../components/Page";
import MediaUploader from "../components/MediaUploader";
import { GetTopic, UpdateTopic } from "../services/api/topic";
import { GetAppResource, AddAppResource } from "../services/api/app";
import { useUserProvider } from "../providers/UserProvider";
import { useModalProvider } from "../providers/ModalProvider";
import { useLoaderProvider } from "../providers/LoaderProvider";
import { useDuplicateValidation, validations } from "../lib/form";
import DataSelector from "../components/DataSelector";
import SubTable from "../components/SubTable";
import contentConfig from "../lib/content_config/config";
import responseProcessor from "../lib/responseProcessor";
import VersionizerWrapper from "../components/Versionizer";
import LimitReached from "../components/modal_contents/LimitReached";
import { setState } from "../lib/form";

const TopicDetail = ({ newTopic, match }) => {
  const contentType = "topics";
  const subTables = contentConfig(contentType).subTables;
  const vals = {
    name: "",
    image_url: "",
    sort_order: 1,
    listing: false,
    directories: [],
    custom_video_text: "",
    video: { id: null },
    custom_article_text: "",
    custom_lesson_text: "",
    custom_resource_text: "",
    custom_action_list_text: "",
    lesson: { id: null },
    article: { id: null },
    sub_topic_items: [],
    tags: [],
    media_metadata: {},
    active: false,
    enterprise: false,
    enterprise_app: null,
    custom_video: { id: null },
    custom_article: { id: null },
    custom_lesson: { id: null },
    custom_action_list: { id: null },
    custom_resource: { id: null },
  };

  const [initialValues, setInitialValues] = useState(vals);
  const [isFormReady, setIsFormReady] = useState(newTopic);
  const [isLoading, setIsLoading] = useState(false);
  const [metadata, setMetadata] = useState({});
  const [data, setData] = useState({ loading: false, data: [], count: 0, meta_data: {} });
  const { user } = useUserProvider();
  const { showModal, closeModal } = useModalProvider();
  const { doSetShowLoadingModal } = useLoaderProvider();
  const uuid = newTopic ? user.currentApp() : match.params.uuid;
  const history = useHistory();
  const platformVersion = parseFloat(user?.app_data?.about?.platform_version);

  const loadData = async () => {
    setData({ ...data, loading: true });
    GetAppResource(user, "topics")
      .then((res) => res.json())
      .then((jsonData) => {
        setData({
          loading: false,
          data: jsonData.data,
          count: jsonData.data.length,
          meta_data: jsonData?.meta_data,
        });
      });
  };

  useEffect(() => {
    if (newTopic) {
      setInitialValues(vals);
      setIsFormReady(true);
    } else {
      setIsLoading(true);
      GetTopic(uuid, user.idToken)
        .then((res) => res.json())
        .then((res) => {
          const data = res?.data;
          const extractContent = (key) => (data?.[key] ? { name: data[key]?.title, id: data[key]?.id } : null);

          const subTopicItemsToSchema = data?.sub_topic_items?.map((item) => {
            if (
              !item.content_id ||
              (platformVersion >= 10.0 && item.item_type === "FeaturedContent")
            ) {
              return item;
            }

            return {
              ...item,
              content_id: {
                id: item.content_id,
                name: item.name
              }
            }
          }) ?? [];

          const initialValues = {
            active: data?.active,
            name: data?.name,
            image_url: data?.image_url,
            sort_order: data?.sort_order,
            listing: data?.listing,
            directories: data?.directories || [],
            custom_video_text: data?.custom_video_text,
            custom_lesson_text: data?.custom_lesson_text,
            custom_article_text: data?.custom_article_text,
            custom_action_list_text: data?.custom_action_list_text,
            custom_resource_text: data?.custom_resource_text,
            custom_video: extractContent("video"),
            custom_article: extractContent("article"),
            custom_lesson: extractContent("lesson"),
            custom_action_list: extractContent("action_list"),
            custom_resource: extractContent("resource"),
            sub_topic_items: subTopicItemsToSchema,
            tags: data?.tags || [],
            enterprise: data?.enterprise,
            enterprise_app: data?.enterprise_app || {},
          };

          setInitialValues(initialValues);
          setMetadata(data?.media_metadata || {});
          setIsLoading(false);
          setIsFormReady(true);
        });
    }

    GetAppResource(user, "categories")
      .then((res) => res.json())
      .then((res) => setState("categoriesRef", JSON.stringify(res.data.filter((category) => category.active))));

    loadData();
  }, []);

  useEffect(() => {
    doSetShowLoadingModal(isLoading);
  }, [isLoading]);

  const validate = useDuplicateValidation(["name"], "sub_topic_items");

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validate: (values) => validate(values, formik),
    validationSchema: () =>
      Yup.object({
        name: validations.str_req_40,
        image_url: Yup.string().max(255),
        sort_order: validations.sort_order,
        listing: Yup.boolean(),
        directories: Yup.array().nullable(),
        tags: Yup.array().nullable(),
        active: Yup.boolean(),
        enterprise: Yup.boolean(),
      }),
    onSubmit: async (values) => {
      setIsLoading(true);
      const sanitizedSubTopicItems = (values.sub_topic_items || [])
        .filter((item) => item && typeof item === "object")
        .map((item) => {
          const isFeaturedContent = item.item_type === "FeaturedContent";
      
          let content_id = item.content_id;
          if (isFeaturedContent && typeof item.content_id === "object" && item.content_id?.id) {
            content_id = item.content_id.id;
          }
      
          const sanitizedItem = {
            ...item,
            ...(isFeaturedContent && { content_id }),
          };
      
          if (!item.id) {
            delete sanitizedItem.id;
          }
      
          return sanitizedItem;
        });

      const payload = {
        ...values,
        sub_topic_items: sanitizedSubTopicItems,
        video: values.custom_video?.id ? { id: values.custom_video.id } : {},
        article: values.custom_article?.id ? { id: values.custom_article.id } : {},
        lesson: values.custom_lesson?.id ? { id: values.custom_lesson.id } : {},
        action_list: values.custom_action_list?.id ? { id: values.custom_action_list.id } : {},
        resource: values.custom_resource?.id ? { id: values.custom_resource.id } : {},
      };

      delete payload.custom_video;
      delete payload.custom_article;
      delete payload.custom_lesson;
      delete payload.custom_action_list;
      delete payload.custom_resource;

      const submit = newTopic
        ? AddAppResource(uuid, "topics", user.idToken, { data: payload })
        : UpdateTopic(uuid, user.idToken, { data: payload });

      submit.then((response) =>
        responseProcessor({
          response,
          showModal,
          onSuccess: () => {
            setIsLoading(false);
            history.push("/topics");
            if (
              newTopic &&
              user?.app_data?.portal?.free_account &&
              data?.meta_data?.total >= data?.meta_data?.limit
            ) {
              showModal(
                <LimitReached
                  onClose={closeModal}
                  freeMessage={user?.app_data?.portal?.free_account_message}
                  appId={user?.currentApp()}
                />, { maxHeight: "70vh", maxWidth: "50vw" });
            }
          },
          onError: () => setIsLoading(false),
        })
      );
    },
  });

  useEffect(() => {
    setState("isEnabledListing", formik.values.listing);
  }, [formik.values])

  if (!isFormReady) {
    return <div>Loading...</div>;
  }

  return (
    <Page
      links={[{ name: "Home", url: "/" }, { name: "Topics/Directory Listings", url: "/topics" }, { name: `${newTopic ? "New" : "Edit"} Topic / Listing`, url: newTopic ? "/topics/new" : `/topics/${uuid}` }]}
      title={`${newTopic ? "New" : "Edit"} Topic / Listing`}
    >
      <EntryForm isLoading={isLoading} formik={formik} submitHandler={formik.handleSubmit} cancelURL="/topics">
        <Column>
          <Input name="name" type="text" label="Name" formik={formik} />
          <MediaUploader name="image_url" label="Image URL" formik={formik} metadata={metadata} mediaType="image" />
          <VersionizerWrapper minVersion={8.5}>
            <VersionizerWrapper minVersion={8} maxVersion={8.5}>
              <div className="italic text-sm text-right font-medium" style={{ marginTop: parseFloat(user.app_data.about.platform_version) >= 8.5 ? 0 : -12 }}>
                Blog Posts and Documents available in app Platform Version 9.0 & up and WEB
              </div>
            </VersionizerWrapper>
          </VersionizerWrapper>
          <CheckBox name="listing" label="Directory Listing" formik={formik} />
          {formik.values?.listing && (
            <DataSelector name="directories" formik={formik} label="Which Directory?" resourceEndpoint="directories" withLegend={(user?.app_data?.setup?.enterprise || user?.app_data?.setup?.reseller) && formik.values.enterprise} filterOptions={((!user?.app_data?.setup?.enterprise && !user?.app_data?.setup?.reseller) || !formik.values.enterprise) ? (values) => values.filter((a) => !a.enterprise) : (values) => values} />
          )}
          <CheckBox name="active" label="Active" formik={formik} />
          <Input name="sort_order" type="number" label="Sort Order" formik={formik} />
          <DataSelector name="tags" formik={formik} label="Tags" resourceEndpoint="tags?category=Content" filterOptions={(values) => values.filter((a) => !a.is_expired)} />
        </Column>
        <Column>
          <VersionizerWrapper minVersion={9}>
            {user?.app_data?.setup?.enterprise && (
              <>
                <CheckBox name="enterprise" label="Business Listing" formik={formik} />
                {formik.values?.enterprise && (
                  <DataSelector name="enterprise_app" formik={formik} label="Which Business?" resourceEndpoint="children" filterOptions={(options) => options.filter((a) => a.active)} single singleSelectOnly withSearch />
                )}
              </>
            )}
          </VersionizerWrapper>
        </Column>

        {subTables &&
          Object.keys(subTables).map((key) => {
            const tableConfig = subTables[key];
            if (!tableConfig) return null;

            return (
              <div key={key} className="w-full mb-5 px-5">
                <SubTable
                  formik={formik}
                  initialValues={initialValues}
                  setInitialValues={setInitialValues}
                  contentConfig={contentConfig(contentType)}
                  subContentType={key}
                  labels={tableConfig.label}
                  data={{
                    loading: false,
                    count: formik.values[key]?.length || 0,
                    data: (formik.values[key] || []).filter(Boolean),
                  }}
                  contentType={contentType}
                  onAddClick={() => {}}
                  loading={false}
                />
              </div>
            );
          })}
      </EntryForm>
    </Page>
  );
};

export default TopicDetail;
